import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  OnInit,
  output,
  signal,
} from '@angular/core';
import {Store} from '@ngrx/store';
import {Observable, Subscription} from 'rxjs';
import {ChallengeBuilderProject, ChallengeObjective, ChallengeTask} from 'src/app/core/model/project';
import {
  ChallengeTasksAction,
  SetBackLoadingAction,
  SetNextLoadingAction,
  UpdateChallengeNextValidAction,
} from 'src/app/core/store/challenge-builder/challenge-builder.action';
import {MessageService} from 'src/app/services/message.service';
import {CbCheckboxOptionComponent, CheckboxOption} from '../shared/cb-checkbox-option.component';

interface LocalTask extends ChallengeTask {
  uid: string;
}

@Component({
  selector: 'cb-tasks',
  standalone: true,
  imports: [CbCheckboxOptionComponent],
  templateUrl: './cb-tasks.component.html',
  styleUrl: './cb-tasks.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CbTasksComponent implements OnInit {
  private static readonly MAX_TASKS_PER_OBJECTIVE = 10;
  private nextClickSubscription?: Subscription;
  private backClickSubscription?: Subscription;
  private readonly store$ = inject(Store);
  private readonly messageService = inject(MessageService);
  private readonly destroyRef = inject(DestroyRef);
  private destroyed = false;
  private uidCounter = 0;

  constructor() {
    this.destroyRef.onDestroy(() => {
      this.destroyed = true;
      this.nextClickSubscription?.unsubscribe();
      this.backClickSubscription?.unsubscribe();
    });
  }

  nextClick$ = input.required<Observable<void>>();
  backClick$ = input.required<Observable<void>>();
  cbProject = input<ChallengeBuilderProject>();

  onNext = output<void>();
  onBack = output<void>();

  // Local state
  objectives = signal<ChallengeObjective[]>([]);
  tasksByObjective = signal<Record<string, LocalTask[]>>({});
  initialFlattenedTasks = signal<ChallengeTask[]>([]);

  selectedObjectives = computed(() => this.objectives().filter(obj => obj.selected));

  optionsByObjective = computed((): Record<string, CheckboxOption[]> => {
    const result: Record<string, CheckboxOption[]> = {};
    const tasksByObj = this.tasksByObjective();

    for (const [objName, tasks] of Object.entries(tasksByObj)) {
      result[objName] = tasks.map((task, index) => ({
        text: 'Add task',
        isChecked: task.selected,
        isInput: true,
        inputIndex: index,
        uid: task.uid,
      }));
    }

    return result;
  });

  hasMultipleTasks = computed((): Record<string, boolean> => {
    const tasksByObj = this.tasksByObjective();
    const result: Record<string, boolean> = {};
    for (const [objName, tasks] of Object.entries(tasksByObj)) {
      result[objName] = tasks.filter(t => t.name.trim().length > 0).length > 1;
    }
    return result;
  });

  duplicateIndicesByObjective = computed((): Record<string, Set<number>> => {
    const tasksByObj = this.tasksByObjective();
    const result: Record<string, Set<number>> = {};

    for (const [objName, tasks] of Object.entries(tasksByObj)) {
      const duplicates = new Set<number>();
      for (let i = 0; i < tasks.length; i++) {
        const val = tasks[i].name.trim().toLowerCase();
        if (!val) continue;
        for (let j = 0; j < tasks.length; j++) {
          if (i === j) continue;
          if (tasks[j].name.trim().toLowerCase() === val) {
            duplicates.add(i);
            break;
          }
        }
      }
      result[objName] = duplicates;
    }

    return result;
  });

  // Validation effect
  // @ts-expect-error - Effect must be assigned to trigger registration
  private readonly validationEffect = effect(
    () => {
      const tasksByObj = this.tasksByObjective();
      const hasDuplicates = Object.values(this.duplicateIndicesByObjective()).some(s => s.size > 0);
      const isValid =
        !hasDuplicates &&
        this.selectedObjectives().every(objective => {
          const tasks = tasksByObj[objective.name] || [];
          return tasks.some(t => t.name.trim().length > 0);
        });
      this.store$.dispatch(new UpdateChallengeNextValidAction({valid: isValid}));
    },
    {allowSignalWrites: true}
  );

  ngOnInit(): void {
    const projectObjectives = this.cbProject()?.challengeBuilder?.objectives || [];
    const projectTasks = this.cbProject()?.challengeBuilder?.tasks || [];

    const taskSelectedMap = new Map(projectTasks.map(t => [t.name, t.selected]));

    const updatedObjectives = projectObjectives.map(obj => ({
      ...obj,
      tasks: obj.tasks?.map(t => ({
        ...t,
        selected: taskSelectedMap.get(t.name) ?? t.selected,
      })),
    }));

    this.objectives.set(structuredClone(updatedObjectives));

    // Build tasks per objective - all tasks shown as inputs
    const tasksByObj: Record<string, LocalTask[]> = {};
    for (const obj of updatedObjectives.filter(o => o.selected)) {
      const tasks: LocalTask[] = (obj.tasks || []).map(t => ({...t, uid: this.generateUid()}));
      tasksByObj[obj.name] = [...tasks, {name: '', selected: false, uid: this.generateUid()}];
    }
    this.tasksByObjective.set(tasksByObj);

    const flattenedTasks = this.flattenSelectedTasksFromObjectives(updatedObjectives);
    this.initialFlattenedTasks.set(structuredClone(flattenedTasks));

    this.nextClickSubscription = this.nextClick$().subscribe(() => this.onNextClick());
    this.backClickSubscription = this.backClick$().subscribe(() => this.onBackClick());
  }

  onTaskToggle(objectiveName: string, option: CheckboxOption, checked: boolean): void {
    this.tasksByObjective.update(tasks => {
      const objTasks = [...(tasks[objectiveName] || [])];
      objTasks[option.inputIndex] = {...objTasks[option.inputIndex], selected: checked};
      return {...tasks, [objectiveName]: this.normalizeTasks(objTasks)};
    });
  }

  onInputChange(objectiveName: string, value: string, index: number): void {
    this.tasksByObjective.update(tasks => {
      const objTasks = [...(tasks[objectiveName] || [])];
      objTasks[index] = {...objTasks[index], name: value, selected: value.trim().length > 0};
      return {...tasks, [objectiveName]: this.normalizeTasks(objTasks)};
    });
  }

  onRemoveTask(objectiveName: string, index: number): void {
    this.tasksByObjective.update(tasks => {
      const objTasks = (tasks[objectiveName] || []).filter((_, i) => i !== index);
      return {...tasks, [objectiveName]: this.normalizeTasks(objTasks)};
    });
  }

  private generateUid(): string {
    return `task-${++this.uidCounter}-${Date.now()}`;
  }

  private normalizeTasks(tasks: LocalTask[]): LocalTask[] {
    const meaningful = tasks.filter(t => t.name.trim().length > 0 || t.selected);
    if (meaningful.length >= CbTasksComponent.MAX_TASKS_PER_OBJECTIVE) {
      return meaningful;
    }
    const existingEmpty = tasks.find(t => !t.name.trim() && !t.selected);
    return [...meaningful, existingEmpty || {name: '', selected: false, uid: this.generateUid()}];
  }

  private flattenTasksFromObjectives(objectives: ChallengeObjective[]): ChallengeTask[] {
    return objectives.flatMap(obj =>
      (obj.tasks || []).map(t => ({
        ...t,
        objectiveId: obj._id,
      }))
    );
  }

  private flattenSelectedTasksFromObjectives(objectives: ChallengeObjective[]): ChallengeTask[] {
    return objectives.flatMap(obj =>
      (obj.tasks || [])
        .filter(t => t.selected)
        .map(t => ({
          ...t,
          objectiveId: obj._id,
        }))
    );
  }

  private getAlteredTasks(allTasks: ChallengeTask[]): ChallengeTask[] {
    const initialTasks = this.initialFlattenedTasks();
    const initialTaskMap = new Map(initialTasks.filter(t => t._id).map(t => [t._id, t]));
    const tasksAltered: ChallengeTask[] = [];

    for (const task of allTasks.filter(t => t.selected)) {
      if (!task._id) {
        tasksAltered.push(task);
      } else {
        const initialTask = initialTaskMap.get(task._id);
        if (!initialTask || initialTask.name !== task.name || !task.metrics?.length) {
          tasksAltered.push(task);
        }
      }
    }
    return tasksAltered;
  }

  private onNextClick(): void {
    const {tasks, tasksAltered} = this.prepareTasks();
    this.saveTasks(tasks, tasksAltered, () => this.onNext.emit());
  }

  private onBackClick(): void {
    const {tasks} = this.prepareTasks();
    this.saveTasks(tasks, [], () => this.onBack.emit());
  }

  private prepareTasks(): {tasks: ChallengeTask[]; tasksAltered: ChallengeTask[]} {
    const tasksByObj = this.tasksByObjective();

    const objectives = this.objectives().map(obj => {
      if (!tasksByObj[obj.name]) return obj;

      const inputTasks = (tasksByObj[obj.name] || [])
        .filter(t => t.name.trim().length > 0)
        .map(t => ({...t, name: t.name.trim()}));

      return {
        ...obj,
        tasks: inputTasks,
      };
    });

    const tasks = this.flattenTasksFromObjectives(objectives);
    const tasksAltered = this.getAlteredTasks(tasks);
    return {tasks, tasksAltered};
  }

  private saveTasks(tasks: ChallengeTask[], tasksAltered: ChallengeTask[], onSuccess?: () => void): void {
    this.store$.dispatch(
      new ChallengeTasksAction({
        projectId: this.cbProject()?._id || '',
        tasks,
        tasksAltered,
        onSuccess: () => {
          if (this.destroyed) return;
          onSuccess?.();
          this.store$.dispatch(new SetNextLoadingAction({nextLoading: false}));
          this.store$.dispatch(new SetBackLoadingAction({backLoading: false}));
        },
        onFailure: (error: Error) => {
          if (this.destroyed) return;
          this.messageService.add('Failed to save tasks', 'error');
          console.error('Failed to save tasks:', error);
          this.store$.dispatch(new SetNextLoadingAction({nextLoading: false}));
          this.store$.dispatch(new SetBackLoadingAction({backLoading: false}));
        },
      })
    );
  }
}
